import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Target, Cpu, Cog } from "lucide-react";

const pillars = [
  {
    icon: Target,
    title: "Strategic Growth",
    description:
      "Aligning digital initiatives with business objectives to unlock new revenue streams and market opportunities.",
    highlights: ["Product Strategy", "Market Expansion", "Revenue Optimization"],
  },
  {
    icon: Cpu,
    title: "MarTech & AI Innovation",
    description:
      "Leveraging cutting-edge technology to automate processes, enhance customer experience, and drive intelligent decision-making.",
    highlights: ["AI/ML Integration", "Marketing Automation", "Data Analytics"],
  },
  {
    icon: Cog,
    title: "Operational Leadership",
    description:
      "Building high-performance teams and streamlined processes that deliver consistent, scalable results.",
    highlights: ["Agile Transformation", "Team Building", "Process Excellence"],
  },
];

export function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" ref={ref} aria-labelledby="about-heading" className="py-20 md:py-28 bg-background">
      <div className="container-wide">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <h2 id="about-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            The Intersection of Strategy & Execution
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            With over 15 years of experience in healthcare and MarTech, I bridge the gap 
            between visionary strategy and practical implementation—turning complex 
            digital transformations into tangible business outcomes.
          </p>
        </motion.div>

        {/* Pillars Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="group relative bg-card rounded-2xl p-8 border border-border shadow-card card-interactive"
            >
              {/* Icon */}
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <pillar.icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-foreground mb-3">
                {pillar.title}
              </h3>

              {/* Description */}
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {pillar.description}
              </p>

              {/* Highlights */}
              <div className="flex flex-wrap gap-2">
                {pillar.highlights.map((highlight) => (
                  <span
                    key={highlight}
                    className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-accent/10 text-accent"
                  >
                    {highlight}
                  </span>
                ))}
              </div>

              {/* Hover accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-accent rounded-b-2xl scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
